import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiEdit } from "react-icons/fi";
import { IoEyeOutline } from "react-icons/io5";
import MasterShell from "../common/MasterShell";
import MasterListPage from "../common/MasterListPage";
import MasterToast from "../common/MasterToast";
import StatusBadge from "../common/StatusBadge";
import useRouteToast from "../common/useRouteToast";
import HierarchyFilterBar from "../../components/report/HierarchyFilterBar";
import { useCapabilities } from "../../contexts/CapabilitiesContext";
import { getDistricts } from "../../services/districtService";
import { getStations } from "../../services/stationService";
import { getStationUsers } from "../../services/stationUserService";
import { getSubDivisions } from "../../services/subDivisionService";
import {
  buildLookupMap,
  extractApiErrorMessage,
  formatMasterDate,
  matchesSearch,
} from "../../utils/masterHelpers";
import { getMasterHierarchyAccess } from "../../utils/hierarchyScope";
import "./Users.css";

const formatRole = (role) =>
  role
    ? role.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
    : "-";

const asArray = (value) => (Array.isArray(value) ? value : []);

export default function UserList() {
  const navigate = useNavigate();
  const { clearToast, toast } = useRouteToast();
  const { canAny, loading: capabilitiesLoading, user } = useCapabilities();
  const canCreateUser = canAny("users", ["create", "manage"]);
  const canReadUser = canAny("users", ["read", "manage"]);
  const canUpdateUser = canAny("users", ["update", "manage"]);
  const [districtLookup, setDistrictLookup] = useState({});
  const [districtOptions, setDistrictOptions] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedDistrictId, setSelectedDistrictId] = useState("");
  const [selectedStationId, setSelectedStationId] = useState("");
  const [selectedSubDivisionId, setSelectedSubDivisionId] = useState("");
  const [stationLookup, setStationLookup] = useState({});
  const [stationOptions, setStationOptions] = useState([]);
  const [subDivisionLookup, setSubDivisionLookup] = useState({});
  const [subDivisionOptions, setSubDivisionOptions] = useState([]);
  const [users, setUsers] = useState([]);

  const scopeAccess = useMemo(
    () => getMasterHierarchyAccess({ user }),
    [user]
  );
  const {
    canSelectDistrict,
    canSelectStation,
    canSelectSubDivision,
    fixedDistrictId,
    fixedStationId,
    fixedSubDivisionId,
  } = scopeAccess;

  const effectiveDistrictId = canSelectDistrict
    ? selectedDistrictId
    : fixedDistrictId;
  const effectiveSubDivisionId = canSelectSubDivision
    ? selectedSubDivisionId
    : fixedSubDivisionId;
  const effectiveStationId = canSelectStation
    ? selectedStationId
    : fixedStationId;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const stationParams = effectiveSubDivisionId
        ? {
            sub_division_id: effectiveSubDivisionId,
            ...(effectiveDistrictId ? { district_id: effectiveDistrictId } : {}),
          }
        : effectiveDistrictId
        ? { district_id: effectiveDistrictId }
        : {};
      const userParams = effectiveStationId
        ? {
            station_id: effectiveStationId,
            ...(effectiveSubDivisionId
              ? { sub_division_id: effectiveSubDivisionId }
              : {}),
            ...(effectiveDistrictId ? { district_id: effectiveDistrictId } : {}),
          }
        : effectiveSubDivisionId
        ? {
            sub_division_id: effectiveSubDivisionId,
            ...(effectiveDistrictId ? { district_id: effectiveDistrictId } : {}),
          }
        : effectiveDistrictId
        ? { district_id: effectiveDistrictId }
        : {};

      const [
        districtsResult,
        subDivisionsResult,
        stationsResult,
        userResponseResult,
      ] = await Promise.allSettled([
        canSelectDistrict ? getDistricts() : Promise.resolve([]),
        canSelectSubDivision
          ? getSubDivisions(
              effectiveDistrictId ? { district_id: effectiveDistrictId } : {}
            )
          : Promise.resolve([]),
        canSelectStation
          ? getStations(stationParams)
          : Promise.resolve([]),
        getStationUsers(userParams),
      ]);

      const districts =
        districtsResult.status === "fulfilled" ? asArray(districtsResult.value) : [];
      const subDivisions =
        subDivisionsResult.status === "fulfilled"
          ? asArray(subDivisionsResult.value)
          : [];
      const stations =
        stationsResult.status === "fulfilled" ? asArray(stationsResult.value) : [];
      const userResponse =
        userResponseResult.status === "fulfilled"
          ? asArray(userResponseResult.value)
          : [];

      setDistrictOptions(districts);
      setSubDivisionOptions(subDivisions);
      setStationOptions(stations);
      setDistrictLookup(buildLookupMap(districts));
      setSubDivisionLookup(buildLookupMap(subDivisions));
      setStationLookup(buildLookupMap(stations));
      setUsers(userResponse);
    } catch (apiError) {
      setError(extractApiErrorMessage(apiError, "Failed to load users."));
      setDistrictLookup({});
      setDistrictOptions([]);
      setSubDivisionLookup({});
      setSubDivisionOptions([]);
      setStationLookup({});
      setStationOptions([]);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [
    canSelectDistrict,
    canSelectStation,
    canSelectSubDivision,
    effectiveDistrictId,
    effectiveStationId,
    effectiveSubDivisionId,
  ]);

  useEffect(() => {
    if (capabilitiesLoading) {
      return;
    }

    fetchData();
  }, [capabilitiesLoading, fetchData]);

  const rows = useMemo(
    () =>
      users.map((item) => ({
        ...item,
        district_name:
          item.district_name ||
          districtLookup[item.district_id] ||
          (item.district_id === user?.district_id ? user?.district_name : "-") ||
          "-",
        role_label: formatRole(item.role),
        station_name: item.station_name || stationLookup[item.station_id] || "-",
        sub_division_name:
          item.sub_division_name ||
          subDivisionLookup[item.sub_division_id] ||
          (item.sub_division_id === user?.sub_division_id
            ? user?.sub_division_name
            : "-") ||
          "-",
      })),
    [districtLookup, stationLookup, subDivisionLookup, user, users]
  );

  const columns = [
    {
      key: "rowNumber",
      label: "Sr. No.",
      render: (_, __, rowNumber) => rowNumber,
    },
    { key: "name", label: "Name" },
    { key: "username", label: "Username" },
    { key: "email", label: "Email" },
    { key: "role_label", label: "Role" },
    { key: "district_name", label: "District" },
    { key: "sub_division_name", label: "Sub-division" },
    { key: "station_name", label: "Station" },
    {
      key: "status",
      label: "Status",
      render: (row) => <StatusBadge value={row.is_active} />,
    },
    {
      key: "last_login",
      label: "Last Login",
      render: (row) => formatMasterDate(row.last_login),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="masters-table__actions">
          {canReadUser ? (
            <button
              className="masters-icon-btn masters-icon-btn--view"
              onClick={() => navigate(`/users/${row.id}/view`)}
              type="button"
            >
              <IoEyeOutline />
            </button>
          ) : null}
          {canUpdateUser && (
            <button
              className="masters-icon-btn masters-icon-btn--edit"
              onClick={() => navigate(`/users/${row.id}/edit`)}
              type="button"
            >
              <FiEdit />
            </button>
          )}
        </div>
      ),
    },
  ];

  const hasHierarchyFilters =
    canSelectDistrict || canSelectSubDivision || canSelectStation;

  return (
    <MasterShell pageClassName="users-module">
      <MasterListPage
        addLabel={canCreateUser ? "Add User" : undefined}
        addPath={canCreateUser ? "/users/add" : undefined}
        className="new_master-records-list"
        columns={columns}
        emptyMessage="User records will appear here after they are created."
        error={error}
        isLoading={loading || capabilitiesLoading}
        onRetry={fetchData}
        rows={rows}
        searchPlaceholder="Search by name, username, email, role, district, sub-division, or station"
        searchPredicate={(row, searchTerm) =>
          matchesSearch(
            [
              row.name,
              row.username,
              row.email,
              row.role_label,
              row.district_name,
              row.sub_division_name,
              row.station_name,
            ],
            searchTerm
          )
        }
        title="Users"
        headerToolbarContent={
          hasHierarchyFilters ? (
            <HierarchyFilterBar
              className="new_report-filter-bar"
              compact
              districtOptions={districtOptions}
              onDistrictChange={(event) => {
                setSelectedDistrictId(event.target.value);
                setSelectedSubDivisionId("");
                setSelectedStationId("");
              }}
              onStationChange={(event) => setSelectedStationId(event.target.value)}
              onSubDivisionChange={(event) => {
                setSelectedSubDivisionId(event.target.value);
                setSelectedStationId("");
              }}
              selectedDistrictId={selectedDistrictId}
              selectedStationId={selectedStationId}
              selectedSubDivisionId={selectedSubDivisionId}
              showDateFilters={false}
              showDistrictFilter={canSelectDistrict}
              showStationFilter={canSelectStation}
              showSubDivisionFilter={canSelectSubDivision}
              stationOptions={stationOptions}
              subDivisionOptions={subDivisionOptions}
            />
          ) : null
        }
      />
      <MasterToast
        message={toast?.message || ""}
        onClose={clearToast}
        open={Boolean(toast)}
        severity={toast?.severity || "success"}
      />
    </MasterShell>
  );
}
